import React, { useState } from 'react';
import { X, Link2, Search } from 'lucide-react';
import { useFamilyStore } from '../../../store/familyStore';

interface LinkMemberModalProps {
  isOpen: boolean;
  memberId: string; // The member whose context menu was opened
  onClose: () => void;
}

type LinkType = 'child' | 'parent' | 'spouse' | 'sibling';

const linkOptions: { type: LinkType; label: string }[] = [
  { type: 'child', label: 'Child' },
  { type: 'parent', label: 'Parent' },
  { type: 'spouse', label: 'Spouse' },
  { type: 'sibling', label: 'Sibling' },
];

export const LinkMemberModal: React.FC<LinkMemberModalProps> = ({
  isOpen,
  memberId,
  onClose,
}) => {
  const { members, linkMembers } = useFamilyStore();
  const [linkType, setLinkType] = useState<LinkType>('child');
  const [searchQuery, setSearchQuery] = useState('');
  const [isLinking, setIsLinking] = useState(false);

  if (!isOpen) return null;

  const sourceMember = members.find(m => m.id === memberId);

  // Exclude self; filter by search
  const candidates = members
    .filter(m => m.id !== memberId)
    .filter(m => {
      if (!searchQuery) return true;
      const q = searchQuery.toLowerCase();
      return m.name.toLowerCase().includes(q) || (m.nickname ?? '').toLowerCase().includes(q);
    })
    .sort((a, b) => a.name.localeCompare(b.name));

  const handleLink = async (targetId: string, name: string) => {
    setIsLinking(true);
    try {
      await linkMembers(memberId, targetId, linkType);
      alert(`Linked ${sourceMember?.name ?? 'member'} as ${linkType} of ${name}`);
      onClose();
    } catch (e) {
      console.error('Failed to link members:', e);
      alert('Failed to link members. Please try again.');
    } finally {
      setIsLinking(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-200">
          <div className="flex items-center space-x-2">
            <Link2 className="w-5 h-5 text-blue-600" />
            <h2 className="text-lg font-semibold text-gray-900">
              Link Member to {sourceMember?.name ?? '...'}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors p-1"
            aria-label="Close modal"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Relationship type selector */}
        <div className="p-5 pb-3">
          <p className="text-sm font-medium text-gray-700 mb-2">Relationship</p>
          <div className="flex flex-wrap gap-2">
            {linkOptions.map(opt => (
              <button
                key={opt.type}
                type="button"
                onClick={() => setLinkType(opt.type)}
                className={`px-4 py-1.5 text-sm rounded-lg border transition-colors ${
                  linkType === opt.type
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Search */}
        <div className="px-5 pb-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search members..."
              className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        {/* Member list */}
        <div className="flex-1 overflow-y-auto px-5 pb-5">
          {candidates.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-8">
              {searchQuery ? 'No members match your search.' : 'No other members to link.'}
            </p>
          ) : (
            <ul className="divide-y divide-gray-100">
              {candidates.map(m => (
                <li key={m.id}>
                  <button
                    type="button"
                    disabled={isLinking}
                    onClick={() => handleLink(m.id, m.name)}
                    className="w-full flex items-center space-x-3 py-2.5 px-2 rounded-lg hover:bg-gray-50 transition-colors text-left disabled:opacity-50"
                  >
                    <div className="w-9 h-9 rounded-full overflow-hidden bg-gray-200 flex items-center justify-center shrink-0">
                      {m.photoUrl ? (
                        <img src={m.photoUrl} alt={m.name} className="w-full h-full object-cover" />
                      ) : (
                        <span className="text-gray-500 text-sm font-medium">{m.name.charAt(0)}</span>
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-gray-900 truncate">{m.name}</p>
                      {m.nickname && <p className="text-xs text-gray-500 truncate">{m.nickname}</p>}
                    </div>
                    <span className="text-xs text-blue-600 shrink-0">{linkType}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};
